import React, { useState, useEffect } from 'react';
import { X, ArrowLeft, ArrowRight } from '@phosphor-icons/react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StatusViewer = ({ customerId, onClose }) => {
  const [allStatuses, setAllStatuses] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    fetchStatuses();
  }, []);

  useEffect(() => {
    // Auto-progress timer (10 seconds per status)
    if (allStatuses.length === 0) return;

    const timer = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          handleNext();
          return 0;
        }
        return prev + 1; // 1% every 100ms = 10 seconds total
      });
    }, 100);

    return () => clearInterval(timer);
  }, [currentIndex, allStatuses]);

  const fetchStatuses = async () => {
    try {
      const response = await axios.get(`${API}/status/all`);
      console.log('✅ Fetched statuses:', response.data);
      console.log('🔍 Backend URL:', BACKEND_URL);
      
      // Log each status for debugging
      response.data.forEach((status, index) => {
        console.log(`Status ${index}:`, {
          type: status.type,
          image_url: status.image_url,
          video_url: status.video_url,
          full_image_path: status.image_url ? `${BACKEND_URL}${status.image_url}` : null
        });
      });
      
      setAllStatuses(response.data);
      
      // Mark first status as viewed
      if (response.data.length > 0) {
        markAsViewed(response.data[0].id);
      }
    } catch (error) {
      console.error('❌ Failed to fetch statuses:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsViewed = async (statusId) => {
    try {
      await axios.post(`${API}/status/${statusId}/view`, null, {
        params: { customer_id: customerId }
      });
    } catch (error) {
      console.error('Failed to mark status as viewed:', error);
    }
  };

  const handleNext = () => {
    if (currentIndex < allStatuses.length - 1) {
      const nextIndex = currentIndex + 1;
      setCurrentIndex(nextIndex);
      setProgress(0);
      markAsViewed(allStatuses[nextIndex].id);
    } else {
      onClose();
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
      setProgress(0);
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const created = new Date(timestamp);
    const diffMs = now - created;
    const diffHrs = Math.floor(diffMs / (1000 * 60 * 60));
    const diffMins = Math.floor(diffMs / (1000 * 60));

    if (diffHrs > 0) return `${diffHrs}h ago`;
    if (diffMins > 0) return `${diffMins}m ago`;
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black flex items-center justify-center z-50">
        <p className="text-white">Loading statuses...</p>
      </div>
    );
  }

  if (allStatuses.length === 0) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center z-50 p-4">
        <p className="text-white text-lg mb-4">No active statuses</p>
        <button
          onClick={onClose}
          className="px-6 py-2 bg-white text-black rounded-lg hover:bg-gray-200 transition-colors"
        >
          Close
        </button>
      </div>
    );
  }

  const currentStatus = allStatuses[currentIndex];

  return (
    <div className="fixed inset-0 bg-black z-50 flex flex-col">
      {/* Header with progress bars */}
      <div className="p-4">
        {/* Progress Bars */}
        <div className="flex gap-1 mb-3">
          {allStatuses.map((_, index) => (
            <div key={index} className="flex-1 h-0.5 bg-gray-600 rounded-full overflow-hidden">
              <div
                className="h-full bg-white transition-all duration-100"
                style={{
                  width: index === currentIndex ? `${progress}%` : index < currentIndex ? '100%' : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* User Info & Close */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center">
              <span className="text-white font-bold text-sm">
                {currentStatus.user_name?.charAt(0).toUpperCase() || 'A'}
              </span>
            </div>
            <div>
              <p className="text-white font-semibold">{currentStatus.user_name}</p>
              <p className="text-gray-300 text-xs">{getTimeAgo(currentStatus.created_at)}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:bg-gray-800 p-2 rounded-full transition-colors"
          >
            <X size={24} weight="bold" />
          </button>
        </div>
      </div>

      {/* Status Content */}
      <div className="flex-1 flex items-center justify-center relative">
        {/* Previous Button */}
        {currentIndex > 0 && (
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors z-10"
          >
            <ArrowLeft size={24} weight="bold" />
          </button>
        )}

        {/* Status Display */}
        <div className="max-w-2xl w-full px-4 flex items-center justify-center" style={{ maxHeight: '80vh' }}>
          {currentStatus.type === 'text' && currentStatus.content ? (
            <div className="bg-wa-panel border-l-4 border-[#00a884] rounded-lg p-8 text-center">
              <p className="text-white text-2xl font-medium leading-relaxed">
                {currentStatus.content}
              </p>
            </div>
          ) : currentStatus.type === 'video' && currentStatus.video_url ? (
            <video
              key={currentStatus.id}
              src={currentStatus.video_url.startsWith('http') ? currentStatus.video_url : `${BACKEND_URL}${currentStatus.video_url.startsWith('/api') ? currentStatus.video_url : '/api' + currentStatus.video_url}`}
              autoPlay
              playsInline
              controls
              className="max-w-full max-h-[70vh] rounded-lg mx-auto"
              onEnded={handleNext}
              onError={(e) => {
                console.error('Video load error:', e);
              }}
            />
          ) : currentStatus.type === 'image' && currentStatus.image_url ? (
            <div className="relative flex items-center justify-center w-full h-full">
              <img
                src={currentStatus.image_url.startsWith('http') ? currentStatus.image_url : `${BACKEND_URL}${currentStatus.image_url.startsWith('/api') ? currentStatus.image_url : '/api' + currentStatus.image_url}`}
                alt="Status"
                className="max-w-full max-h-[75vh] rounded-lg object-contain cursor-zoom-in"
                style={{ margin: '0 auto' }}
                onClick={(e) => {
                  // Toggle zoom on click
                  const img = e.target;
                  if (img.style.transform === 'scale(1.5)') {
                    img.style.transform = 'scale(1)';
                    img.style.cursor = 'zoom-in';
                  } else {
                    img.style.transform = 'scale(1.5)';
                    img.style.cursor = 'zoom-out';
                  }
                }}
                onError={(e) => {
                  console.error('Image load error for status:', currentStatus.image_url);
                  e.target.style.display = 'none';
                }}
              />
              {/* Caption - WhatsApp style blur bg */}
              {currentStatus.caption && (
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 backdrop-blur-sm p-3 rounded-b-lg">
                  <p className="text-white text-sm text-center">{currentStatus.caption}</p>
                </div>
              )}
            </div>
          ) : currentStatus.type === 'audio' && currentStatus.audio_url ? (
            <div className="bg-[#1f2c34] p-8 rounded-2xl max-w-sm mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-4">
                <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40" fill="#22c55e" viewBox="0 0 256 256"><path d="M178.37,96.33a8,8,0,0,1,1.3,11.27A83.91,83.91,0,0,1,128,132a83.91,83.91,0,0,1-51.67-24.4,8,8,0,0,1,12.57-9.94A67.92,67.92,0,0,0,128,116a67.92,67.92,0,0,0,39.1-18.37A8,8,0,0,1,178.37,96.33Z"/></svg>
              </div>
              <audio
                src={currentStatus.audio_url.startsWith('http') ? currentStatus.audio_url : `${BACKEND_URL}${currentStatus.audio_url}`}
                controls
                autoPlay
                className="w-full"
                onEnded={handleNext}
              />
              {currentStatus.caption && (
                <p className="text-white text-sm mt-4">{currentStatus.caption}</p>
              )}
            </div>
          ) : (
            <div className="bg-gray-800 text-gray-400 p-8 rounded-lg text-center">
              <p>Content not available</p>
            </div>
          )}
        </div>

        {/* Next Button */}
        {currentIndex < allStatuses.length - 1 && (
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-black/50 text-white p-3 rounded-full hover:bg-black/70 transition-colors z-10"
          >
            <ArrowRight size={24} weight="bold" />
          </button>
        )}
      </div>

      {/* View Count */}
      <div className="p-4 text-center">
        <p className="text-gray-400 text-sm">
          {currentIndex + 1} / {allStatuses.length}
        </p>
      </div>
    </div>
  );
};

export default StatusViewer;
